import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { formatCurrency, formatDate } from "@/lib/utils";
import {
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Activity,
  Brain,
  RefreshCw,
  Link as LinkIcon,
  Trophy,
  FileText,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { data: stats, isLoading, isError, refetch } = trpc.admin.dashboard.stats.useQuery();
  const { data: billingStats, isLoading: billingLoading } = trpc.admin.billing.stats.useQuery();
  const { data: connectors } = trpc.admin.dashboard.connectors.useQuery();

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Tour de contrôle</h1>
            <p className="text-muted-foreground">Vue transversale de la plateforme Constrigo</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {isError && (
          <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
            <AlertCircle className="h-10 w-10 text-destructive/40" />
            <p className="text-muted-foreground">Erreur de chargement</p>
            <Button variant="outline" onClick={() => refetch()}>Réessayer</Button>
          </div>
        )}

        {/* BLOC 1 — Tenants */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            Tenants
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Link to="/tenants">
              <Card className="hover:bg-accent cursor-pointer transition-colors">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  {/* Lot 1 : sens explicites — « clientes SaaS » (lifecycle
                      canonique) sur « inscrites » = identités Company toutes
                      populations (SaaS + TravauxBTP + autres). */}
                  <CardTitle className="text-sm font-medium">Clientes SaaS</CardTitle>
                  <Building2 className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  {isLoading ? <Skeleton className="h-8 w-16" /> : (
                    <>
                      <div className="text-2xl font-bold">{stats?.saasClientCompaniesCount ?? "—"}</div>
                      <p className="text-xs text-muted-foreground">sur {stats?.companiesCount ?? "—"} inscrites (toutes populations)</p>
                    </>
                  )}
                </CardContent>
              </Card>
            </Link>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Utilisateurs inscrits (toutes populations)</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-16" /> : (
                  <div className="text-2xl font-bold">{stats?.usersCount ?? "—"}</div>
                )}
              </CardContent>
            </Card>

            <Link to="/tenants?filter=no_categories">
              <Card className={`hover:bg-accent cursor-pointer transition-colors ${(stats?.companiesWithoutCategories ?? 0) > 0 ? "border-warning/30" : ""}`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Non configurées</CardTitle>
                  <AlertCircle className="h-4 w-4 text-warning" />
                </CardHeader>
                <CardContent>
                  {isLoading ? <Skeleton className="h-8 w-16" /> : (
                    <>
                      <div className="text-2xl font-bold text-warning">{stats?.companiesWithoutCategories ?? 0}</div>
                      <p className="text-xs text-muted-foreground">Sans catégories</p>
                    </>
                  )}
                </CardContent>
              </Card>
            </Link>

            <Card className={(stats?.companiesWithExpiredDocs ?? 0) > 0 ? "border-destructive/30" : ""}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Docs expirés</CardTitle>
                <AlertCircle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-16" /> : (
                  <>
                    <div className="text-2xl font-bold text-destructive">{stats?.companiesWithExpiredDocs ?? 0}</div>
                    <p className="text-xs text-muted-foreground">Entreprises à relancer</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* BLOC 2 — Revenue */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-success" />
            Revenue
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Link to="/revenue">
              <Card className="hover:bg-accent cursor-pointer transition-colors">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">CA Service (mois)</CardTitle>
                  <DollarSign className="h-4 w-4 text-success" />
                </CardHeader>
                <CardContent>
                  {billingLoading ? <Skeleton className="h-8 w-24" /> : (
                    <div className="text-2xl font-bold text-success">
                      {formatCurrency(billingStats?.serviceRevenue ?? 0)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Assistance (à encaisser)</CardTitle>
                <TrendingUp className="h-4 w-4 text-warning" />
              </CardHeader>
              <CardContent>
                {billingLoading ? <Skeleton className="h-8 w-24" /> : (
                  <>
                    <div className="text-2xl font-bold text-warning">
                      {formatCurrency(billingStats?.assistanceDueTotal ?? 0)}
                    </div>
                    <p className="text-xs text-muted-foreground">{billingStats?.assistanceDueCount ?? 0} commandes</p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Success fees (à encaisser)</CardTitle>
                <Trophy className="h-4 w-4 text-warning" />
              </CardHeader>
              <CardContent>
                {billingLoading ? <Skeleton className="h-8 w-24" /> : (
                  <>
                    <div className="text-2xl font-bold text-warning">
                      {formatCurrency(billingStats?.successFeeDueTotal ?? 0)}
                    </div>
                    <p className="text-xs text-muted-foreground">{billingStats?.successFeeDueCount ?? 0} factures</p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Taux de gain</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-success" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-16" /> : (
                  <>
                    <div className="text-2xl font-bold">{stats?.winRate ?? 0}%</div>
                    <p className="text-xs text-muted-foreground">Projets gagnés</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* BLOC 3 — Activité plateforme */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Activité plateforme
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">AO (24h)</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-12" /> : (
                  <div className="text-2xl font-bold">{stats?.tendersLast24h ?? 0}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">AO (7j)</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-12" /> : (
                  <div className="text-2xl font-bold">{stats?.tendersLast7Days ?? 0}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Projets en cours</CardTitle>
                <TrendingUp className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-12" /> : (
                  <div className="text-2xl font-bold">{stats?.projectsInProgress ?? 0}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Support ouverts</CardTitle>
                <AlertCircle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-12" /> : (
                  <>
                    <div className="text-2xl font-bold">{stats?.supportTicketsOpen ?? 0}</div>
                    {(stats?.supportTicketsUrgent ?? 0) > 0 && (
                      <p className="text-xs text-destructive">{stats?.supportTicketsUrgent} urgents</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* BLOC 4 — Connecteurs + AI */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Connecteurs */}
          <Card>
            <CardHeader className="flex flex-row items-center gap-2 pb-2">
              <LinkIcon className="h-4 w-4" />
              <CardTitle className="text-base">Connecteurs d'ingestion</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {!connectors ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : connectors.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun connecteur configuré</p>
              ) : (
                connectors.map((c) => (
                  <div key={c.name} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.lastRunAt ? `Dernier: ${formatDate(c.lastRunAt)}` : "Jamais exécuté"}
                      </div>
                    </div>
                    <Badge variant={c.status === "SUCCESS" ? "success" : c.status === "FAILED" ? "destructive" : "secondary"}>
                      {c.status ?? "N/A"}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* AI Usage quick view */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">AI Usage</CardTitle>
              </div>
              <Link to="/ai-usage">
                <Button variant="ghost" size="sm">Voir détails</Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center h-20 text-muted-foreground text-sm">
                <div className="text-center">
                  <Brain className="h-8 w-8 mx-auto mb-2 text-primary/40" />
                  <p>Métriques IA disponibles sur la page AI Usage</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
